import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function ExteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Exterior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Camarillo', 'Thousand Oaks', 'Ventura County', 'Los Angeles'],
    description: 'Professional mobile exterior car detailing services including hand wash, clay bar treatment, wheel cleaning, and wax protection.'
  };
  const faqs = [{
    question: 'What is included in exterior detailing?',
    answer: 'Our exterior detail includes a safe hand wash, deep wheel and tire cleaning, clay bar decontamination, bug and tar removal, premium wax or sealant application, and tire and trim dressing.'
  }, {
    question: 'Will exterior detailing remove swirl marks?',
    answer: 'A standard exterior detail cleans and protects but does not remove swirl marks. For deeper gloss and lasting protection, pair it with our ceramic coating service.'
  }, {
    question: 'How long does exterior protection last?',
    answer: 'Our hybrid ceramic wax and polymer sealants typically provide several months of protection and water beading, depending on conditions and how often the vehicle is washed.'
  }, {
    question: 'How much does exterior detailing cost?',
    answer: 'Exterior detailing starts at $160, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you bring your own water?',
    answer: 'Yes. We are fully self-contained with our own spot-free water and power, so we can detail your vehicle anywhere in Ventura County.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Exterior Detailing',
    item: 'https://www.fulldetailpro.com/services/exterior-detailing'
  }];
  return <ServicePageLayout title="Exterior Detailing Ventura County" subtitle="Protect your paint and restore a brilliant shine with our premium mobile exterior detailing services. We bring the ultimate wash and wax directly to you." metaDescription="Top-rated mobile exterior detailing in Ventura County \u2014 Camarillo, Oxnard & Ventura. Hand wash, clay bar & ceramic wax protection. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-603">
      <p data-id="element-604">
        Your vehicle's exterior faces a constant barrage of environmental
        hazards: UV rays, bird droppings, tree sap, road tar, and industrial
        fallout. Over time, these contaminants degrade your clear coat, leaving
        your paint looking dull and feeling rough. At Mike's Pro Detail, our{' '}
        <strong data-id="element-605">mobile exterior car detailing</strong> service is designed to
        safely remove these contaminants, restore a deep gloss, and apply
        durable protection. Whether you need car detailing in Camarillo,
        Thousand Oaks, or anywhere in Ventura County, our mobile unit provides a
        superior alternative to automated car washes that scratch and swirl your
        paint.
      </p>

      <h2 data-id="element-606">Our Exterior Detailing Process</h2>
      <p data-id="element-607">
        We utilize safe, scratch-free wash methods and premium products to
        ensure your vehicle looks its absolute best. Our exterior detailing
        package includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-608">
        <li className="flex items-start" data-id="element-609">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-610" />
          <span data-id="element-611">
            A meticulous hand wash using premium microfiber towels and
            pH-neutral soap to safely lift dirt without inducing swirl marks.
          </span>
        </li>
        <li className="flex items-start" data-id="element-612">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-613" />
          <span data-id="element-614">
            <strong data-id="element-615">Deep Wheel & Tire Cleaning:</strong> We use specialized
            brushes and non-acidic cleaners to remove baked-on brake dust from
            wheel barrels, faces, and tires.
          </span>
        </li>
        <li className="flex items-start" data-id="element-616">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-617" />
          <span data-id="element-618">
            <strong data-id="element-619">Clay Bar Treatment:</strong> A crucial step that removes
            embedded contaminants (like overspray and rail dust) that washing
            alone cannot remove, leaving the paint smooth as glass.
          </span>
        </li>
        <li className="flex items-start" data-id="element-620">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-621" />
          <span data-id="element-622">
            <strong data-id="element-623">Bug & Tar Removal:</strong> Safe breakdown and removal of
            stubborn bug splatter and road tar from the front bumper and lower
            rocker panels.
          </span>
        </li>
        <li className="flex items-start" data-id="element-624">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-625" />
          <span data-id="element-626">
            <strong data-id="element-627">Premium Wax / Sealant Application:</strong> Application of a
            high-quality hybrid ceramic wax or polymer sealant to provide months
            of protection and intense water beading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-628">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-629" />
          <span data-id="element-630">
            <strong data-id="element-631">Tire & Trim Dressing:</strong> Application of a no-sling,
            UV-blocking dressing to tires and faded exterior plastics to restore
            a rich, dark appearance.
          </span>
        </li>
      </ul>

      <h2 data-id="element-632">Why Exterior Detailing is Essential</h2>
      <p data-id="element-633">
        Regular exterior detailing is preventative maintenance for your
        vehicle's paint. The Southern California sun can quickly cause clear
        coat failure and oxidation if the paint is left unprotected. By
        regularly removing bonded contaminants through a clay bar treatment and
        applying a protective wax or sealant, you create a sacrificial barrier
        against the elements. This not only keeps your car looking stunning but
        also preserves its resale value and prevents costly paint repairs down
        the line.
      </p>

      <h2 data-id="element-634">The Premier Choice for Mobile Auto Detailing Camarillo CA</h2>
      <p data-id="element-635">
        When you search for "Mobile Detailing Ventura County" or need reliable
        service in Thousand Oaks, Mike's Pro Detail is the trusted choice. We
        bring our own spot-free water and electricity, meaning we can perform a
        flawless exterior detail in your driveway, at your office parking lot,
        or even at your apartment complex. We treat every vehicle as if it were
        our own, ensuring that every inch of your exterior receives the
        attention it deserves.
      </p>

      <h2 data-id="element-636">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-637">
        <Link to="/services/ceramic-coating" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-638">
          Ceramic Coating &rarr;
        </Link>
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-639">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-640">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}